// @ts-nocheck

// ─── Re-export shared constants ─────────────────────────────────────────────
export {
	STORES_TABLE_ID,
	SKUS_TABLE_ID,
	WAREHOUSES_TABLE_ID,
	ORDER_STATUS_PARAMS,
	EXCLUDED_FILTER_COLUMNS,
	COLUMN_EXCLUDE_KW,
	COLUMN_WAREHOUSE_KW,
	COLUMN_STORE_KW,
	COLUMN_ORDER_KW,
	PAGE_SIZE,
	MAX_BACKGROUND_PAGES,
	DEBOUNCE_SAVE_MS,
	VALIDATION_ERROR_DISPLAY_MS,
	AVAILABILITY_CAP_ERROR_MS,
	BULK_ITEMS_PER_PAGE,
	SELECT_ROW_KEY,
	GRID_HEADER_HEIGHT,
	GRID_ROW_HEIGHT,
	GRID_FONT_SIZE,
	GRID_MIN_COL_WIDTH,
} from 'pages/orderShared/constants';

// ─── Variant-specific constants ─────────────────────────────────────────────

/** AG Grid table ID for Order SKUs data */
export const ORDER_SKUS_TABLE_ID = 1;

export const PREFIX = 'orderBySku';
export const DEFAULT_EXPORT_FILENAME = 'OrderBySKU_Export';

/** Mass order method options */
export const MASS_ORDER_METHODS = [
	{ displayName: 'Flat — Same qty to all stores', value: 'flat' },
	{ displayName: 'YTD S/T tiered — Based on YTD sell-through tier', value: 'ytdST' },
	{ displayName: '28-day S/T — Based on 28-day sell-through', value: 'd28ST' },
	{ displayName: 'Store Volume — Based on store volume tier', value: 'storeVol' },
	{ displayName: 'L7 Sales — Based on last 7 days sales', value: 'l7Sales' },
	{ displayName: 'Aggregated Spread — Distribute total across stores', value: 'aggSpread' },
];

export const COLUMN_SKU_KW = [
	'sku',
	'genus',
	'brand',
	'size',
	'season',
	'tabletype',
	'table type',
	'subclass',
	'subcategory',
	'consignment',
	'isc',
	'parent sku',
	'erp sku',
	'sa4',
	'sa5',
	'sa6',
];

/** Fields to always exclude from auto-discovery (internal IDs, timestamps, system cols, SKU-level cols) */
export const EXCLUDED_GRID_FIELDS = new Set([
	'OrderDetailID',
	'BulkOrderDetailTmpID',
	'OrderID',
	'OrderKitID',
	'SKUID',
	'UOMID',
	'PlanogramAttributeID',
	'AdDetailID',
	'HealthCheckID',
	'InitialDataSource',
	'InitialUpdate',
	'DataSource',
	'LastUpdate',
	'LastUpdateUser',
	'OrderQty',
	'IsReadOnly',
	'_availError',
	/* SKU-level fields — grid is at store-order level, these are the same for every row */
	'SKUCost',
	'SKURetail',
	'SKUDescription',
	'SKUName',
	'SKU',
	'UPC',
	'UOMConversion',
	'OrderQtyEaches',
	'OrderStorageUnitQty',
	'StorageUnitQty',
	/* SKU attributes & other SKU-level metadata */
	'SKUAttribute1',
	'SKUAttribute2',
	'SKUAttribute3',
	'SKUAttribute4',
	'SKUAttribute5',
	'SKUAttribute6',
	'SKUAttribute7',
	'SKUAttribute8',
	'SKUAttribute9',
	'SKUAttribute10',
	'IsPBS',
	'ParentSKU',
	'ParentSKUDescription',
	'SubCategory',
	'IsActive',
	'Size',
	'Brand',
	'Genus',
	'OnOrder',
	'InTransit',
	'Cost',
	'AdTitle',
]);

/* ─── AG Grid Column Config ─── */
export const COL_CONFIG = [
	{ field: 'OrderBatchID', headerName: 'Batch ID', width: 90, enableRowGroup: true },
	{ field: 'Store', headerName: 'Store', width: 80, enableRowGroup: true },
	{ field: 'StoreDescription', headerName: 'Name', width: 140 },
	{ field: 'Area', headerName: 'Area', width: 90, enableRowGroup: true },
	{ field: 'Buyer', headerName: 'Buyer', width: 100, enableRowGroup: true },
	{ field: 'VolumeClass', headerName: 'Vol', width: 60, enableRowGroup: true },
	{ field: 'Warehouse', headerName: 'WH', width: 80, enableRowGroup: true },
	{ field: 'OnHand', headerName: 'OH', width: 70, type: 'numericColumn' },
	{ field: 'ProjectedOnHand', headerName: 'Proj OH', width: 80, type: 'numericColumn' },
	{ field: 'OnHandInTransit', headerName: 'In Trans', width: 80, type: 'numericColumn' },
	{ field: 'SuggestedQty', headerName: 'Sugg', width: 70, type: 'numericColumn' },
	{
		field: 'CurrentWOS',
		headerName: 'WOS',
		width: 70,
		type: 'numericColumn',
		valueFormatter: (p: any) => (p.value != null ? Number(p.value).toFixed(1) : ''),
	},
	{
		field: 'ProjectedWOS',
		headerName: 'P.WOS',
		width: 70,
		type: 'numericColumn',
		valueFormatter: (p: any) => (p.value != null ? Number(p.value).toFixed(1) : ''),
	},
	{
		field: 'CurrentProjectedWOS',
		headerName: 'CP WOS',
		width: 80,
		type: 'numericColumn',
		hide: true,
		valueFormatter: (p: any) => (p.value != null ? Number(p.value).toFixed(1) : ''),
	},
	{ field: 'Leading7DayAIForecastQty', headerName: '7d Fcst', width: 80, type: 'numericColumn' },
	{ field: 'Leading14DayAIForecastQty', headerName: '14d Fcst', width: 80, type: 'numericColumn' },
	{ field: 'Leading21DayAIForecastQty', headerName: '21d Fcst', width: 80, type: 'numericColumn', hide: true },
	{ field: 'Leading14DayNaiveForecastQty', headerName: '14d Naive', width: 85, type: 'numericColumn', hide: true },
	{ field: '30DayNaiveForecastQty', headerName: '30d Naive', width: 85, type: 'numericColumn', hide: true },
	{ field: 'Trailing14DaySalesQty', headerName: 'T14 Sls', width: 80, type: 'numericColumn' },
	{ field: 'Trailing28DaySalesQty', headerName: 'T28 Sls', width: 80, type: 'numericColumn' },
	{
		field: 'Trailing14DaySalesRetail',
		headerName: 'T14 $',
		width: 80,
		type: 'numericColumn',
		hide: true,
		valueFormatter: (p: any) => (p.value != null ? '$' + Number(p.value).toFixed(2) : ''),
	},
	{
		field: 'Trailing28DaySalesRetail',
		headerName: 'T28 $',
		width: 80,
		type: 'numericColumn',
		hide: true,
		valueFormatter: (p: any) => (p.value != null ? '$' + Number(p.value).toFixed(2) : ''),
	},
	{ field: 'DaysSinceLastSales', headerName: 'DsLS', width: 70, type: 'numericColumn', hide: true },
	{ field: 'CurrentAvailableQty', headerName: 'Avail', width: 70, type: 'numericColumn' },
	{
		field: 'IsReadOnly',
		headerName: 'Locked',
		width: 70,
		cellRenderer: (p: any) => (p.value ? '🔒' : ''),
		enableRowGroup: false,
	},
	/* OrderQty is appended dynamically in columnDefs useMemo to inject shelfQty step + availability max */
];

/** Quick-group button definitions */
export const GROUP_OPTIONS = [
	{ field: 'VolumeClass', label: 'Volume' },
	{ field: 'Warehouse', label: 'Warehouse' },
	{ field: 'Store', label: 'Store' },
];
